import { Button, Cell, Grid, Icon, Tag, Tooltip } from 'bold-ui'
import useSession from 'components/auth/useSession'
import { Form, FormRenderProps } from 'components/form'
import { ProcedimentoSelectField, ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import { GrupoProcedimentoEnum, TipoProcedimentoEnum } from 'graphql/types.generated'
import React from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { EditableList, EditableRow, useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

export interface ProcedimentoSigtapFieldModel {
  _id: ID
  procedimento: ProcedimentoSelectModel
  automatico?: boolean
}

export interface ProcedimentoSigtapFieldProps<T> {
  name: MetaArray<ProcedimentoSigtapFieldModel>
  cidadao?: CidadaoAtendimento
}

const meta = metaPath<ProcedimentoSigtapFieldModel>()

export function ProcedimentoSigtapField<T = any>(props: ProcedimentoSigtapFieldProps<T>) {
  const { name, cidadao } = props
  const { data } = useSession()

  const {
    handleSubmit,
    removeItem,
    input: { value },
  } = useEditableListField({
    name,
  })

  const procedimentoIsEqual = (procedimento: ProcedimentoSelectModel) => {
    return (value || []).find((o2) => procedimento.id === o2.procedimento.id)
  }

  const renderForm = (props: FormRenderProps<ProcedimentoSigtapFieldModel>) => {
    const handleChange = (newValue: ProcedimentoSelectModel) => {
      newValue && !procedimentoIsEqual(newValue) && props.form.submit()
    }

    return (
      <>
        <ProcedimentoSelectField
          name={meta.procedimento}
          label='Procedimentos administrativos (SIGTAP)'
          filtroPadrao={true}
          tipoProcedimento={TipoProcedimentoEnum.ADMINISTRATIVO}
          gruposProcedimento={[
            GrupoProcedimentoEnum.ACOES_PROMOCAO_PREVENCAO_SAUDE,
            GrupoProcedimentoEnum.FINALIDADE_DIAGNOSTICA,
            GrupoProcedimentoEnum.CLINICOS,
            GrupoProcedimentoEnum.CIRURGICO,
          ]}
          sexo={isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : undefined}
          dataNascimento={cidadao?.dataNascimento}
          cbo={data.acesso.__typename === 'Lotacao' && data.acesso.cbo.id}
          itemIsEqual={procedimentoIsEqual}
          onChange={handleChange}
        />
      </>
    )
  }

  return (
    <Grid gapVertical={0.5}>
      <Cell size={8}>
        <Form<ProcedimentoSigtapFieldModel> render={renderForm} onSubmit={handleSubmit} {...props} />
      </Cell>

      {value?.length > 0 && (
        <Cell size={12}>
          <EditableList>
            {value.map((item) => (
              <ProcedimentoRow key={item._id} model={item} onRemove={removeItem} />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

interface ProcedimentoRowProps {
  model: ProcedimentoSigtapFieldModel
  onRemove(itemToRemove: ProcedimentoSigtapFieldModel): void
}

function ProcedimentoRow(props: ProcedimentoRowProps) {
  const { model, onRemove } = props

  const handleRemove = () => onRemove(model)

  return (
    <EditableRow editing={false}>
      <Grid style={{ margin: 0 }}>
        <Cell size={10} alignSelf='center'>
          {`${model.procedimento?.descricao} - ${model.procedimento?.codigo}`}
          {model.automatico && <Tag style={{ marginLeft: '0.5rem' }}>Adicionado automaticamente</Tag>}
        </Cell>

        <Cell size={2} alignSelf='center' style={{ padding: '0.25rem 0', textAlign: 'right' }}>
          <Tooltip text='Excluir'>
            <Button size='small' skin='ghost' onClick={handleRemove}>
              <Icon icon='trashOutline' />
            </Button>
          </Tooltip>
        </Cell>
      </Grid>
    </EditableRow>
  )
}
